import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  DEFAULT_LOCALE,
  pluralKey,
  resolveInitialLocale,
  splitTemplate,
  storeLocale,
  translate,
  type SupportedLocale,
  type TranslationVars,
} from './index';

/** Values a caller may drop into a `{{placeholder}}` when the result is rendered, not measured. */
export type NodeVars = Record<string, ReactNode>;

interface LocaleContextValue {
  readonly locale: SupportedLocale;
  /** Translates to a string. Use this for everything that is not markup. */
  readonly t: (key: string, vars?: TranslationVars) => string;
  /**
   * Translates the `_one` / `_other` variant of a key, passing `count` as a variable too.
   *
   * Saves every caller repeating `count === 1 ? … : …` at the call site, where the wrong branch
   * is invisible until a queue happens to hold exactly one job.
   */
  readonly tCount: (
    key: string,
    count: number,
    vars?: TranslationVars,
  ) => string;
  /**
   * Translates to nodes, so one catalogue string can carry inline markup.
   *
   * The markup stays in the JSX — `tNode('backfill.lead', { auditLog: <code>…</code> })` — which
   * is what keeps `<` and `>` out of the catalogue (a parity spec forbids them: a translation
   * string carrying markup was destroyed by a `textContent` write once already) while still
   * leaving the sentence one translatable unit whose word order German may reorder freely.
   */
  readonly tNode: (key: string, vars: NodeVars) => ReactNode;
  /** Switches locale and persists the choice. */
  readonly setLocale: (locale: SupportedLocale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * Locale state for the whole tool.
 *
 * Mounted above the router, so the sign-in screen — which renders outside `Layout` and therefore
 * outside the header — is translated too and carries its own switcher.
 *
 * There is no URL involvement, unlike the portal's provider: an ops URL is pasted into an incident
 * channel and opened by whoever is on call, and a `?locale=` riding along would hand them the
 * language of the person who copied the link rather than their own.
 */
export function LocaleProvider({ children }: { readonly children: ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    resolveInitialLocale(navigator.language),
  );

  const setLocale = useCallback((next: SupportedLocale) => {
    setLocaleState(next);
    // Persisting HERE and nowhere else is what makes an explicit pick outrank the browser
    // permanently: `resolveInitialLocale` only ever reads.
    storeLocale(next);
  }, []);

  const value = useMemo<LocaleContextValue>(() => {
    const t = (key: string, vars?: TranslationVars) =>
      translate(locale, key, vars);
    return {
      locale,
      t,
      tCount: (key, count, vars) =>
        t(pluralKey(key, count), { count, ...vars }),
      tNode: (key, vars) =>
        splitTemplate(translate(locale, key)).map((part, index) =>
          'text' in part ? (
            part.text
          ) : (
            <Fragment key={`${part.name}-${index}`}>
              {/* An unfilled hole shows as the placeholder, exactly as `interpolate` leaves it. */}
              {vars[part.name] ?? `{{${part.name}}}`}
            </Fragment>
          ),
        ),
      setLocale,
    };
  }, [locale, setLocale]);

  return (
    <LocaleContext.Provider value={value}>
      {/*
        Keeps `<html lang>` in step with the copy, which matters for screen readers and for
        hyphenation of the long German compounds this domain is full of.
      */}
      <DocumentLanguage locale={locale} />
      {children}
    </LocaleContext.Provider>
  );
}

/** Sets `<html lang>` as a side effect of rendering, with no effect hook to forget. */
function DocumentLanguage({ locale }: { readonly locale: SupportedLocale }) {
  if (document.documentElement.lang !== locale) {
    document.documentElement.lang = locale;
  }
  return null;
}

/** Locale and translators for the current screen. */
export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    // A missing provider is a wiring bug, not a runtime condition to degrade through.
    throw new Error('useLocale must be used inside a LocaleProvider');
  }
  return context;
}

/** Re-exported so components need one import for the common case. */
export { DEFAULT_LOCALE };
